// SPDX-License-Identifier: MIT
// Pure seams for cross-workstation sync of agent BUILD-SESSION history.
// Only session/request METADATA travels to the cloud (prompt, kind, timestamps,
// a short summary) — NEVER the raw NDJSON feed (requests[].lines): it's MBs per
// run and a full build transcript (code/files). The detail feed stays LOCAL ;
// the built work itself lives in the git repo (code continuity). These seams
// strip/rehydrate/merge that metadata. Wiring lives in run-cloud-sync.ts.
import type { Session, AgentRequest } from './session-policy';

// Cloud shape (matches public.project_sessions ; snake_case, no `lines`).
export interface CloudSessionRequest {
  id: string;
  prompt: string;
  kind: 'build' | 'followup';
  started_at: string;
  summary: string | null;
}
export interface CloudSession {
  id: string;
  project_id?: string;
  started_at: string;
  requests: CloudSessionRequest[];
}

const SUMMARY_MAX = 280;

// Derive a short human summary of a request from its raw feed: the text of the
// last `result` line. null when the feed has no result (incomplete/errored run).
export function summarizeRequest(lines: string[]): string | null {
  for (let i = lines.length - 1; i >= 0; i--) {
    try {
      const o = JSON.parse(lines[i]) as { type?: string; result?: string };
      if (o.type === 'result' && typeof o.result === 'string') {
        return o.result.slice(0, SUMMARY_MAX);
      }
    } catch { /* not JSON — skip */ }
  }
  return null;
}

// Local session → cloud metadata (drops every `lines` feed, adds a summary).
export function stripSessionForCloud(session: Session): CloudSession {
  return {
    id: session.id,
    started_at: session.startedAt,
    requests: session.requests.map((r) => ({
      id: r.id,
      prompt: r.prompt,
      kind: r.kind,
      started_at: r.startedAt,
      summary: summarizeRequest(r.lines),
    })),
  };
}

// Cloud metadata → local session with an EMPTY feed (the detail is local-only;
// a session built on another machine shows its prompts but no raw transcript).
export function cloudSessionToLocal(c: CloudSession): Session {
  return {
    id: c.id,
    startedAt: c.started_at,
    requests: c.requests.map((r): AgentRequest => ({
      id: r.id,
      prompt: r.prompt,
      kind: r.kind,
      startedAt: r.started_at,
      lines: [],
    })),
  };
}

// Merge cloud sessions into the local list: union by session id, LOCAL wins
// (it carries the real feed) ; cloud-only sessions are added as metadata. Sorted
// by startedAt so the history reads chronologically across machines.
export function mergeSessions(local: Session[], cloud: CloudSession[]): Session[] {
  const byId = new Map<string, Session>();
  for (const s of local) byId.set(s.id, s);
  for (const c of cloud) if (!byId.has(c.id)) byId.set(c.id, cloudSessionToLocal(c));
  return [...byId.values()].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
}
